import React, { useMemo, useState } from 'react';
import { Trash2, RefreshCw, Plus } from 'lucide-react';
import { useBoqCostIndex } from '../hooks/useBoqCostIndex';
import { BrioCard, BrioButton, BrioInput, BrioSelect, BrioEmptyState } from '../../components/BrioComponents';
import { ToolbarIcon } from '../components/ToolbarIcon';
import { StickyScrollBox } from '../components/StickyScrollbar';

// ============================================================================
// Cost Index — the construction escalation series that prices old library
// rates onto a new BOQ date (adjusted = original × index(today) ÷ index(rate
// date)). General consumer CPI is too blunt for construction, so this is a
// dedicated construction index per region. Replace the firm-estimate seed with
// WPI/CPWD (India) or CBS bouwkosten (NL) figures — only the year-over-year
// ratios matter, the base level is arbitrary.
// ============================================================================
const REGIONS = ['IN', 'NL'];
const REGION_LABEL = { IN: 'India (INR)', NL: 'Netherlands (EUR)' };

const cell = { padding: '0.3rem 0.5rem' };
const numCell = { ...cell, textAlign: 'right' };
const edNum = { width: 90, textAlign: 'right', padding: '0.2rem', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4 };

function CostIndexPage() {
  const idx = useBoqCostIndex();
  const [form, setForm] = useState({ region: 'IN', year: '', index_value: '', source: 'manual', note: '' });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const byRegion = useMemo(() => {
    const m = new Map();
    REGIONS.forEach((r) => m.set(r, []));
    idx.rows.forEach((row) => {
      if (!m.has(row.region)) m.set(row.region, []);
      m.get(row.region).push(row);
    });
    // newest year first within each region
    m.forEach((rows) => rows.sort((a, b) => b.year - a.year));
    return m;
  }, [idx.rows]);

  const updateField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPoint = async () => {
    setFormError(null);
    setSaving(true);
    const result = await idx.upsert(form);
    setSaving(false);
    if (result.success) {
      setForm((f) => ({ ...f, year: '', index_value: '', note: '' }));
    } else {
      setFormError(result.error || 'Failed to save.');
    }
  };

  const editValue = async (row, raw) => {
    const v = Number(raw);
    if (raw === '' || !Number.isFinite(v) || v === Number(row.value)) return;
    await idx.upsert({ ...row, index_value: v });
  };

  const del = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${row.region} ${row.year}?`)) return;
    await idx.remove(row.id);
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--brio-text-muted)', maxWidth: 760 }}>
        Yearly construction cost index used to inflation-adjust library rates onto a new BOQ pricing date
        (<strong>adjusted = original × index(today) ÷ index(rate&nbsp;date)</strong>). This is a construction-specific
        index — distinct from general consumer CPI, whose basket tracks construction costs only loosely. Replace the
        firm-estimate seed with WPI / CPWD (India) or CBS bouwkosten (NL). Only the ratios between years matter; the
        base level is arbitrary.
      </div>

      {/* Add a point */}
      <BrioCard style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.6rem', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.8rem' }}>
            <div style={{ color: 'var(--brio-text-muted)', marginBottom: 2 }}>Region</div>
            <BrioSelect value={form.region} onChange={updateField('region')} style={{ minWidth: 160 }}>
              {REGIONS.map((r) => <option key={r} value={r}>{REGION_LABEL[r]}</option>)}
            </BrioSelect>
          </label>
          <label style={{ fontSize: '0.8rem' }}>
            <div style={{ color: 'var(--brio-text-muted)', marginBottom: 2 }}>Year</div>
            <BrioInput type="number" value={form.year} onChange={updateField('year')} placeholder="2026" style={{ width: 90 }} />
          </label>
          <label style={{ fontSize: '0.8rem' }}>
            <div style={{ color: 'var(--brio-text-muted)', marginBottom: 2 }}>Index value</div>
            <BrioInput type="number" step="0.1" value={form.index_value} onChange={updateField('index_value')} placeholder="126.0" style={{ width: 110 }} />
          </label>
          <label style={{ fontSize: '0.8rem' }}>
            <div style={{ color: 'var(--brio-text-muted)', marginBottom: 2 }}>Source</div>
            <BrioInput value={form.source} onChange={updateField('source')} placeholder="WPI / CPWD / CBS" style={{ width: 150 }} />
          </label>
          <label style={{ fontSize: '0.8rem', flex: 1, minWidth: 140 }}>
            <div style={{ color: 'var(--brio-text-muted)', marginBottom: 2 }}>Note (optional)</div>
            <BrioInput value={form.note} onChange={updateField('note')} style={{ width: '100%' }} />
          </label>
          <BrioButton variant="primary" onClick={addPoint} disabled={saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.8rem' }}>
            <Plus size={14} /> {saving ? 'Saving…' : 'Add / update'}
          </BrioButton>
        </div>
        {formError && <div style={{ color: '#a94442', marginTop: '0.5rem', fontSize: '0.82rem' }}>{formError}</div>}
        <div style={{ fontSize: '0.75rem', color: 'var(--brio-text-muted)', marginTop: '0.5rem' }}>
          Adding a region + year that already exists overwrites that point.
        </div>
      </BrioCard>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <ToolbarIcon onClick={idx.refresh} tooltip="Refresh"><RefreshCw size={15} /></ToolbarIcon>
      </div>

      {idx.error && <div style={{ color: '#a94442', marginBottom: '0.75rem' }}>{idx.error}</div>}

      {idx.loading ? (
        <BrioCard><div style={{ color: 'var(--brio-text-muted)', padding: '0.5rem' }}>Loading cost index…</div></BrioCard>
      ) : idx.rows.length === 0 ? (
        <BrioCard>
          <BrioEmptyState>
            <div style={{ marginBottom: '0.5rem' }}>No cost index points yet.</div>
            <div style={{ fontSize: '0.85rem' }}>Add a point above for each region and year you want to escalate rates against.</div>
          </BrioEmptyState>
        </BrioCard>
      ) : (
        REGIONS.filter((r) => (byRegion.get(r) || []).length > 0).map((region) => (
          <BrioCard key={region} style={{ marginBottom: '1rem' }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              {REGION_LABEL[region]} <span style={{ fontWeight: 400, color: 'var(--brio-text-muted)' }}>· {(byRegion.get(region) || []).length} year{(byRegion.get(region) || []).length === 1 ? '' : 's'}</span>
            </div>
            <StickyScrollBox style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--brio-border, #ddd)', color: 'var(--brio-text-muted)' }}>
                    <th style={cell}>Year</th>
                    <th style={numCell}>Index</th>
                    <th style={numCell}>YoY</th>
                    <th style={cell}>Source</th>
                    <th style={cell}>Note</th>
                    <th style={cell}>Updated</th>
                    <th style={cell} />
                  </tr>
                </thead>
                <tbody>
                  {(byRegion.get(region) || []).map((row, i, arr) => {
                    const prev = arr[i + 1]; // arr is newest-first, so next index is the older year
                    const yoy = prev && prev.value ? (row.value / prev.value - 1) * 100 : null;
                    return (
                      <tr key={row.id} style={{ borderBottom: '1px solid var(--brio-border-subtle, #eee)' }}>
                        <td style={cell}>{row.year}</td>
                        <td style={numCell}>
                          <input type="number" step="0.1" defaultValue={row.value}
                            onBlur={(e) => editValue(row, e.target.value)} style={edNum} />
                        </td>
                        <td style={{ ...numCell, color: yoy == null ? 'var(--brio-text-muted)' : (yoy >= 0 ? '#3f7d4f' : '#a94442') }}>
                          {yoy == null ? '—' : `${yoy >= 0 ? '+' : ''}${yoy.toFixed(1)}%`}
                        </td>
                        <td style={cell}>{row.source || '—'}</td>
                        <td style={{ ...cell, color: 'var(--brio-text-muted)' }}>{row.note || '—'}</td>
                        <td style={{ ...cell, color: 'var(--brio-text-muted)', fontSize: '0.76rem' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : '—'}</td>
                        <td style={cell}>
                          <ToolbarIcon onClick={() => del(row)} danger tooltip="Delete point"><Trash2 size={14} /></ToolbarIcon>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </StickyScrollBox>
          </BrioCard>
        ))
      )}
    </div>
  );
}

export default CostIndexPage;
